import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Briefcase,
  MapPin,
  CheckCircle2,
  Loader2,
  Upload,
  FileText,
  X,
  Sparkles,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UF_OPTIONS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS",
  "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC",
  "SP", "SE", "TO",
];

const SEXO_OPTIONS = [
  { value: "masculino", label: "Masculino" },
  { value: "feminino", label: "Feminino" },
  { value: "outro", label: "Outro" },
];

const ESTADO_CIVIL_OPTIONS = [
  { value: "solteiro", label: "Solteiro(a)" },
  { value: "casado", label: "Casado(a)" },
  { value: "divorciado", label: "Divorciado(a)" },
  { value: "viuvo", label: "Viúvo(a)" },
  { value: "uniao_estavel", label: "União estável" },
];

const EMPTY_PERSONAL_DATA = {
  rg: "",
  rg_orgao_emissor: "",
  rg_data_expedicao: "",
  cpf: "",
  sexo: "",
  estado_civil: "",
  data_nascimento: "",
  cnh: "",
  cnh_categoria: "",
  cnh_data_expedicao: "",
  naturalidade: "",
  naturalidade_uf: "",
  nacionalidade: "",
  nome_mae: "",
  nome_pai: "",
};

const PERSONAL_DATA_LABELS = {
  rg: "RG",
  rg_orgao_emissor: "Órgão emissor",
  rg_data_expedicao: "Data de expedição do RG",
  cpf: "CPF",
  sexo: "Sexo",
  estado_civil: "Estado civil",
  data_nascimento: "Data de nascimento",
  cnh: "CNH",
  cnh_categoria: "Categoria da CNH",
  cnh_data_expedicao: "Data de expedição da CNH",
  naturalidade: "Naturalidade",
  naturalidade_uf: "UF de naturalidade",
  nacionalidade: "Nacionalidade",
  nome_mae: "Nome da mãe",
  nome_pai: "Nome do pai",
};

const PublicJobDetailPage = ({ orgMode = false }) => {
  const { slug, jobSlug } = useParams();
  const [searchParams] = useSearchParams();
  const basePath = orgMode ? "orgs" : "companies";
  const backToListPath = orgMode ? `/carreiras/org/${slug}` : `/carreiras/${slug}`;
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [personalData, setPersonalData] = useState(EMPTY_PERSONAL_DATA);
  const [semCnh, setSemCnh] = useState(false);
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/public/${basePath}/${slug}/jobs/${jobSlug}`
        );
        setJob(res.data);
      } catch (e) {
        setError(
          e?.response?.status === 404
            ? "Vaga não encontrada ou indisponível."
            : "Erro ao carregar a vaga."
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug, jobSlug, basePath]);

  const onFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Envie um PDF");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Arquivo muito grande (máx. 10 MB)");
      return;
    }
    setResume(file);
  };

  const setPersonalField = (field, value) =>
    setPersonalData((prev) => ({ ...prev, [field]: value }));

  const CNH_FIELDS = ["cnh", "cnh_categoria", "cnh_data_expedicao"];

  const toggleSemCnh = (checked) => {
    setSemCnh(checked);
    if (checked) {
      setPersonalData((prev) => ({
        ...prev,
        cnh: "Não possuo",
        cnh_categoria: "",
        cnh_data_expedicao: "",
      }));
    } else {
      setPersonalData((prev) => ({ ...prev, cnh: "", cnh_categoria: "", cnh_data_expedicao: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !resume) {
      toast.error("Preencha nome, e-mail e anexe seu currículo em PDF");
      return;
    }
    const requiredPersonalFields = Object.keys(EMPTY_PERSONAL_DATA).filter(
      (field) => !(semCnh && CNH_FIELDS.includes(field))
    );
    const missingField = requiredPersonalFields.find(
      (field) => !personalData[field].trim()
    );
    if (missingField) {
      toast.error(`Preencha o campo "${PERSONAL_DATA_LABELS[missingField]}"`);
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", name.trim());
      fd.append("email", email.trim());
      fd.append("phone", phone.trim());
      Object.entries(personalData).forEach(([field, value]) => {
        fd.append(field, value.trim());
      });
      fd.append("resume", resume, resume.name);
      // Carry over UTM tags from the URL so HR knows which channel
      // the candidate came from (?utm_source=whatsapp, etc.)
      const utmSource = (searchParams.get("utm_source") || "").trim();
      const utmMedium = (searchParams.get("utm_medium") || "").trim();
      const utmCampaign = (searchParams.get("utm_campaign") || "").trim();
      if (utmSource) fd.append("utm_source", utmSource);
      if (utmMedium) fd.append("utm_medium", utmMedium);
      if (utmCampaign) fd.append("utm_campaign", utmCampaign);
      await axios.post(
        `${BACKEND_URL}/api/public/${basePath}/${slug}/jobs/${jobSlug}/apply`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }
      );
      setSubmitted(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Erro ao enviar candidatura. Tente novamente."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="h-8 w-8 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="bg-white border border-slate-200 rounded-lg p-8 max-w-md text-center">
          <h1 className="heading text-xl font-semibold text-slate-900 mb-2">
            {error || "Vaga não encontrada"}
          </h1>
          <Link to={backToListPath} className="text-sm text-slate-600 underline">
            Voltar para a lista de vagas
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="public-job-detail-page" className="min-h-screen bg-slate-50">
      <Helmet>
        <title>{`${job.title} — ${job.company_name} · GridRecruiter`}</title>
        <meta
          name="description"
          content={`${job.location ? job.location + " · " : ""}${job.employment_type}${job.salary_range ? " · " + job.salary_range : ""} — ${(job.description || "").slice(0, 140)}`}
        />
        <meta property="og:title" content={`${job.title} — ${job.company_name}`} />
        <meta property="og:description" content={(job.description || "").slice(0, 200)} />
        <meta property="og:type" content="website" />
        {job.company_cover_url && (
          <meta
            property="og:image"
            content={`${typeof window !== "undefined" ? window.location.origin : ""}${job.company_cover_url}`}
          />
        )}
        {!job.company_cover_url && job.company_logo_url && (
          <meta
            property="og:image"
            content={`${typeof window !== "undefined" ? window.location.origin : ""}${job.company_logo_url}`}
          />
        )}
        <meta name="twitter:card" content={job.company_cover_url ? "summary_large_image" : "summary"} />
      </Helmet>
      {/* Top bar */}
      <header
        className={`relative overflow-hidden text-white ${
          job.company_cover_url
            ? ""
            : "bg-gradient-to-br from-blue-950 via-slate-950 to-emerald-950"
        }`}
      >
        {job.company_cover_url && (
          <>
            <img
              src={job.company_cover_url}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover opacity-60"
              data-testid="public-detail-cover"
            />
            <div
              className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/80 to-slate-950/70"
              aria-hidden="true"
            />
          </>
        )}
        <div className="relative max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to={backToListPath}
            className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"
            data-testid="back-to-careers"
          >
            <ArrowLeft className="w-4 h-4" />
            Carreiras {job.company_name}
          </Link>
          <div className="flex items-center gap-3">
            {job.company_logo_url && (
              <div className="w-9 h-9 rounded-md bg-white/95 p-1 shadow ring-1 ring-white/20 hidden sm:flex items-center justify-center">
                <img
                  src={job.company_logo_url}
                  alt={`Logo ${job.company_name}`}
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            <div className="text-xs uppercase tracking-[0.18em] text-slate-400 hidden sm:block">
              {job.company_name}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
        {/* Job details */}
        <article>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">
            Vaga aberta
          </div>
          <h1 className="heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-950 leading-tight">
            {job.title}
          </h1>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-4 text-sm text-slate-600">
            {job.department && <span>{job.department}</span>}
            {job.location && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {job.location}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Briefcase className="w-3.5 h-3.5" />
              {job.employment_type}
            </span>
            {job.experience_years > 0 && (
              <span>{job.experience_years}+ anos de experiência</span>
            )}
            {job.salary_range && (
              <span className="font-medium text-slate-700">{job.salary_range}</span>
            )}
          </div>

          <section className="mt-8">
            <h2 className="heading text-lg font-semibold text-slate-900 mb-2">
              Sobre a vaga
            </h2>
            <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">
              {job.description}
            </p>
          </section>

          {job.requirements?.length > 0 && (
            <section className="mt-8">
              <h2 className="heading text-lg font-semibold text-slate-900 mb-2">
                Requisitos
              </h2>
              <ul className="text-sm text-slate-700 space-y-1.5 list-disc pl-5">
                {job.requirements.map((r, i) => (
                  <li key={`req-${r.slice(0, 30)}-${i}`}>{r}</li>
                ))}
              </ul>
            </section>
          )}

          {job.skills?.length > 0 && (
            <section className="mt-8">
              <h2 className="heading text-lg font-semibold text-slate-900 mb-2">
                Habilidades desejadas
              </h2>
              <div className="flex flex-wrap gap-1.5">
                {job.skills.map((s, i) => (
                  <span
                    key={`${s}-${i}`}
                    className="px-2.5 py-1 bg-white text-slate-700 text-xs rounded-md border border-slate-200"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </section>
          )}
        </article>

        {/* Apply form */}
        <aside className="lg:sticky lg:top-6 self-start">
          {submitted ? (
            <div
              data-testid="application-success"
              className="bg-white border border-emerald-200 rounded-xl p-6 text-center"
            >
              <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="heading text-lg font-bold text-slate-950 mb-2">
                Candidatura recebida!
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-5">
                Obrigado, {name.split(" ")[0]}! Vamos analisar seu currículo com a
                ajuda da IA e entrar em contato se houver compatibilidade.
              </p>
              <Link
                to={backToListPath}
                className="text-sm font-medium text-slate-700 hover:text-slate-950 underline"
              >
                Ver outras vagas
              </Link>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="apply-form"
              className="bg-white border border-slate-200 rounded-xl p-6"
            >
              <div className="mb-5">
                <div className="inline-flex items-center gap-1.5 mb-2 px-2.5 py-1 rounded-md bg-brand-blue text-white text-[11px] font-semibold uppercase tracking-wider">
                  <Sparkles className="w-3 h-3" />
                  Análise por IA
                </div>
                <h3 className="heading text-lg font-bold text-slate-950 leading-tight">
                  Quero me candidatar
                </h3>
                <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
                  Preencha rapidamente. Você recebe um retorno em poucos dias.
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="apply-name" className="text-xs">Nome completo</Label>
                  <Input
                    id="apply-name"
                    data-testid="apply-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="Maria Silva"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="apply-email" className="text-xs">E-mail</Label>
                  <Input
                    id="apply-email"
                    data-testid="apply-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="voce@email.com"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="apply-phone" className="text-xs">
                    WhatsApp <span className="text-slate-400">(opcional)</span>
                  </Label>
                  <Input
                    id="apply-phone"
                    data-testid="apply-phone-input"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(11) 99999-9999"
                  />
                </div>

                <div className="pt-2 border-t border-slate-100">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
                    Dados pessoais
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="apply-data-nascimento" className="text-xs">Data de nascimento</Label>
                      <Input
                        id="apply-data-nascimento"
                        type="date"
                        value={personalData.data_nascimento}
                        onChange={(e) => setPersonalField("data_nascimento", e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="apply-sexo" className="text-xs">Sexo</Label>
                      <Select
                        value={personalData.sexo}
                        onValueChange={(v) => setPersonalField("sexo", v)}
                      >
                        <SelectTrigger id="apply-sexo">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {SEXO_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="apply-estado-civil" className="text-xs">Estado civil</Label>
                      <Select
                        value={personalData.estado_civil}
                        onValueChange={(v) => setPersonalField("estado_civil", v)}
                      >
                        <SelectTrigger id="apply-estado-civil">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {ESTADO_CIVIL_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="apply-nacionalidade" className="text-xs">Nacionalidade</Label>
                      <Input
                        id="apply-nacionalidade"
                        value={personalData.nacionalidade}
                        onChange={(e) => setPersonalField("nacionalidade", e.target.value)}
                        placeholder="Brasileira"
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="apply-naturalidade" className="text-xs">Naturalidade</Label>
                      <Input
                        id="apply-naturalidade"
                        value={personalData.naturalidade}
                        onChange={(e) => setPersonalField("naturalidade", e.target.value)}
                        placeholder="Cidade de nascimento"
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="apply-naturalidade-uf" className="text-xs">UF de naturalidade</Label>
                      <Select
                        value={personalData.naturalidade_uf}
                        onValueChange={(v) => setPersonalField("naturalidade_uf", v)}
                      >
                        <SelectTrigger id="apply-naturalidade-uf">
                          <SelectValue placeholder="UF" />
                        </SelectTrigger>
                        <SelectContent>
                          {UF_OPTIONS.map((uf) => (
                            <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="apply-nome-mae" className="text-xs">Nome da mãe</Label>
                      <Input
                        id="apply-nome-mae"
                        value={personalData.nome_mae}
                        onChange={(e) => setPersonalField("nome_mae", e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="apply-nome-pai" className="text-xs">Nome do pai</Label>
                      <Input
                        id="apply-nome-pai"
                        value={personalData.nome_pai}
                        onChange={(e) => setPersonalField("nome_pai", e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="apply-cpf" className="text-xs">CPF</Label>
                      <Input
                        id="apply-cpf"
                        value={personalData.cpf}
                        onChange={(e) => setPersonalField("cpf", e.target.value)}
                        placeholder="000.000.000-00"
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="apply-rg" className="text-xs">RG</Label>
                      <Input
                        id="apply-rg"
                        value={personalData.rg}
                        onChange={(e) => setPersonalField("rg", e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="apply-rg-orgao" className="text-xs">Órgão emissor</Label>
                      <Input
                        id="apply-rg-orgao"
                        value={personalData.rg_orgao_emissor}
                        onChange={(e) => setPersonalField("rg_orgao_emissor", e.target.value)}
                        placeholder="SSP/UF"
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="apply-rg-data" className="text-xs">Data de expedição (RG)</Label>
                      <Input
                        id="apply-rg-data"
                        type="date"
                        value={personalData.rg_data_expedicao}
                        onChange={(e) => setPersonalField("rg_data_expedicao", e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="apply-cnh" className="text-xs">CNH</Label>
                        <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
                          <input
                            type="checkbox"
                            checked={semCnh}
                            onChange={(e) => toggleSemCnh(e.target.checked)}
                            data-testid="apply-sem-cnh-checkbox"
                          />
                          Não possuo
                        </label>
                      </div>
                      <Input
                        id="apply-cnh"
                        value={personalData.cnh}
                        onChange={(e) => setPersonalField("cnh", e.target.value)}
                        disabled={semCnh}
                        required={!semCnh}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="apply-cnh-categoria" className="text-xs">Categoria (CNH)</Label>
                      <Input
                        id="apply-cnh-categoria"
                        value={personalData.cnh_categoria}
                        onChange={(e) => setPersonalField("cnh_categoria", e.target.value)}
                        placeholder="AB"
                        disabled={semCnh}
                        required={!semCnh}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="apply-cnh-data" className="text-xs">Data de expedição (CNH)</Label>
                      <Input
                        id="apply-cnh-data"
                        type="date"
                        value={personalData.cnh_data_expedicao}
                        onChange={(e) => setPersonalField("cnh_data_expedicao", e.target.value)}
                        disabled={semCnh}
                        required={!semCnh}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5 pt-2 border-t border-slate-100">
                  <Label className="text-xs">Currículo (PDF)</Label>
                  <input
                    ref={inputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    onChange={(e) => onFile(e.target.files?.[0])}
                    data-testid="apply-resume-input"
                  />
                  {!resume ? (
                    <button
                      type="button"
                      data-testid="apply-pick-resume"
                      onClick={() => inputRef.current?.click()}
                      className="w-full border-2 border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-50 rounded-lg p-4 text-center transition-colors"
                    >
                      <Upload className="w-5 h-5 text-slate-500 mx-auto mb-1" />
                      <div className="text-sm text-slate-700 font-medium">
                        Clique para selecionar
                      </div>
                      <div className="text-[11px] text-slate-500">PDF até 10 MB</div>
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md p-2.5">
                      <div className="w-8 h-8 rounded bg-red-50 border border-red-100 flex items-center justify-center text-red-600 shrink-0">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0 text-sm text-slate-900 truncate">
                        {resume.name}
                      </div>
                      <button
                        type="button"
                        onClick={() => setResume(null)}
                        className="text-slate-400 hover:text-red-600 p-1"
                        data-testid="apply-remove-resume"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  data-testid="apply-submit"
                  disabled={submitting}
                  className="w-full bg-brand-gradient text-white border-0"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    "Enviar candidatura"
                  )}
                </Button>

                <p className="text-[10px] text-slate-500 leading-relaxed text-center">
                  Ao se candidatar, você concorda em ter seu currículo analisado por IA
                  para fins de recrutamento desta vaga.
                </p>
              </div>
            </form>
          )}
        </aside>
      </main>

      <footer className="border-t border-slate-200 bg-white mt-12">
        <div className="max-w-4xl mx-auto px-6 py-6 text-xs text-slate-500 text-center">
          Página de carreiras gerada por <strong className="text-slate-700">GridRecruiter</strong>
        </div>
      </footer>
    </div>
  );
};

export default PublicJobDetailPage;
